import { preventMinification } from '@/@core/utils/preventMinification';
import { useUserStore } from '@/stores/userStore';
import { telemetry, FaroTelemetryPlugin, TelemetryVuePlugin } from '@aleph-alpha/lib-telemetry';
import { User } from 'oidc-client-ts';
import { ref, watch, nextTick, type App } from 'vue';

export async function setupTelemetry(app: App, faroApiKey: string) {
  const faroUrl = preventMinification(import.meta.env.VITE_FARO_TELEMETRY_URL);
  const faroFallbackUrl = preventMinification(import.meta.env.VITE_FARO_TELEMETRY_FALLBACK_URL);
  const otlpUrl = preventMinification(import.meta.env.VITE_OTEL_COLLECTOR_URL);
  const otlpFallbackUrl = preventMinification(import.meta.env.VITE_OTEL_COLLECTOR_FALLBACK_URL);
  const excludeBaggageUrls = preventMinification(import.meta.env.VITE_IAM_ISSUER);
  const appName = preventMinification(
    import.meta.env.INSTRUMENTATION_APP_IDENTIFIER ?? 'assistant-ui'
  );
  const appVersion = preventMinification(import.meta.env.VITE_APP_VERSION ?? '1.0.0');
  const environment = preventMinification(import.meta.env.VITE_ENVIRONMENT);
  const hostDomain = window.location.host;

  app.use(
    TelemetryVuePlugin,
    FaroTelemetryPlugin({
      app: {
        name: appName,
        version: appVersion,
        release: appVersion,
        environment: environment,
      },
      debug: false,
      url: faroUrl,
      fallbackUrl: faroFallbackUrl,
      otlpUrl: otlpUrl,
      otlpFallbackUrl: otlpFallbackUrl,
      apiKey: faroApiKey,
      excludeBaggageUrls: excludeBaggageUrls,
      includeBaggageUrls: hostDomain,
    })
  );

  // Wait for Vue's next tick to ensure plugin installation is complete,
  // then add a reasonable delay for the async telemetry initialization.
  // This prevents the race condition where user callbacks fire before telemetry is ready.
  await nextTick();
  await new Promise((resolve) => setTimeout(resolve, 500)); // 500ms should be sufficient for most cases

  console.debug('Telemetry setup complete, initializing user session tracking');

  const userStore = useUserStore();
  const lastIdentifiedUserId = ref<string | null>(null);

  watch(
    () => userStore.user,
    async (user: User | null) => {
      if (user?.profile?.sub && user.profile.sub !== lastIdentifiedUserId.value) {
        // Extract user ID from the user profile
        const userId = user.profile.sub;

        // Extract session ID from user session_state or generate a new one
        const sessionId = user.session_state || crypto.randomUUID();

        // Initialize session baggage with proper user and session IDs
        telemetry.setUserSessionBaggage(userId, sessionId);
        lastIdentifiedUserId.value = userId;

        console.debug('Telemetry session baggage initialized', { userId, sessionId });
      }
    },
    { immediate: true }
  );
}
